import { Spinner, SpinnerSize } from '@fluentui/react';
import { ClassNamesFromIStyles, useClassNames, useTheme } from '@h2oai/ui-kit';
import {
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  Plugin,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { forwardRef } from 'react';
import { Line } from 'react-chartjs-2';
import { ChartJSOrUndefined } from 'react-chartjs-2/dist/types';

import { ILineChartStyles, lineChartStylesDefault, loadingSpinnerStyles } from './LineChart.styles';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface ILineChartProps {
  data: ChartData<'line'> | null;
  options: ChartOptions<'line'>;
  plugins?: Plugin<'line'>[];
  loading?: boolean;
  error?: boolean;
}

const emptyData: ChartData<'line'> = {
  labels: [],
  datasets: [
    {
      label: '',
      data: [],
      backgroundColor: 'rgba(0, 0, 0, 0)',
    },
  ],
};

const LineChart = forwardRef<ChartJSOrUndefined<'line'>, ILineChartProps>(
  ({ data, options, plugins, loading, error }, ref) => {
    const classNames = useClassNames<ILineChartStyles, ClassNamesFromIStyles<ILineChartStyles>>(
      'line-chart',
      lineChartStylesDefault
    );

    const theme = useTheme();
    const hasData =
      data && data.datasets.length > 0 && data.datasets.some((dataset) => dataset.data && dataset.data.length > 0);

    const chartData = hasData ? data : emptyData;

    return (
      <div className={classNames.lineChartRoot}>
        <div className={classNames.lineChartContainer}>
          <Line data={chartData} options={options} ref={ref} plugins={plugins} />
        </div>

        {!hasData && !loading && !error && (
          <div className={classNames.overlayContainer}>
            <div className={classNames.overlayText}>No Data</div>
          </div>
        )}

        {loading && (
          <div className={classNames.overlayContainer}>
            <Spinner
              styles={loadingSpinnerStyles(theme)}
              className={classNames.loadingSpinner}
              size={SpinnerSize.large}
              label="Loading..."
            />
          </div>
        )}

        {error && (
          <div className={classNames.overlayContainer}>
            <div className={classNames.overlayErrorText}>An error occurred</div>
          </div>
        )}
      </div>
    );
  }
);

export default LineChart;
